/**
 * 图片优化工具
 * 提供图片压缩、格式转换、尺寸调整等功能
 */

export interface ImageOptimizeOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1
  format?: 'jpeg' | 'png' | 'webp';
}

/**
 * 压缩图片
 * @param file 原始文件
 * @param options 优化选项
 * @returns 压缩后的 Blob
 */
export const compressImage = async (
  file: File,
  options: ImageOptimizeOptions = {}
): Promise<Blob> => {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    format = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // 计算新尺寸
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        // 创建 canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('无法获取 canvas context'));
          return;
        }

        // 优化渲染质量
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // 绘制图片
        ctx.drawImage(img, 0, 0, width, height);

        // 转换为 Blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('图片压缩失败'));
            }
          },
          `image/${format}`,
          quality
        );
      };

      img.onerror = () => reject(new Error('图片加载失败'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });
};

/**
 * 生成缩略图
 * @param file 原始文件
 * @param size 缩略图尺寸
 * @returns 缩略图 Blob
 */
export const generateThumbnail = async (
  file: File,
  size: number = 200
): Promise<Blob> => {
  return compressImage(file, {
    maxWidth: size,
    maxHeight: size,
    quality: 0.7,
    format: 'jpeg'
  });
};

/**
 * 检测图片格式
 * @param file 文件
 * @returns 图片格式
 */
export const detectImageFormat = (file: File): string => {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext || 'unknown';
};

/**
 * 检测是否需要压缩
 * @param file 文件
 * @param maxSize 最大文件大小（字节）
 * @returns 是否需要压缩
 */
export const needsCompression = (file: File, maxSize: number = 1024 * 1024): boolean => {
  return file.size > maxSize;
};

/**
 * 获取图片尺寸
 * @param file 文件
 * @returns 图片尺寸
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      
      img.onerror = () => reject(new Error('无法读取图片尺寸'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });
};

/**
 * 批量压缩图片
 * @param files 文件数组
 * @param options 优化选项
 * @returns 压缩后的 Blob 数组
 */
export const compressImages = async (
  files: File[],
  options: ImageOptimizeOptions = {}
): Promise<Blob[]> => {
  const promises = files.map(file => compressImage(file, options));
  return Promise.all(promises);
};

/**
 * 转换 Blob 为 File
 * @param blob Blob 对象
 * @param fileName 文件名
 * @returns File 对象
 */
export const blobToFile = (blob: Blob, fileName: string): File => {
  return new File([blob], fileName, { type: blob.type });
};

/**
 * 预加载图片
 * @param urls 图片 URL 数组
 * @returns Promise
 */
export const preloadImages = (urls: string[]): Promise<void[]> => {
  const promises = urls.map(url => {
    return new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  });
  
  return Promise.all(promises);
};

/**
 * 检测 WebP 支持
 * @returns 是否支持 WebP
 */
export const supportsWebP = (): boolean => {
  const canvas = document.createElement('canvas');
  if (canvas.getContext && canvas.getContext('2d')) {
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }
  return false;
};

/**
 * 获取优化后的图片格式
 * @param originalFormat 原始格式
 * @returns 优化后的格式
 */
export const getOptimalFormat = (originalFormat: string): 'jpeg' | 'png' | 'webp' => {
  if (supportsWebP()) {
    return 'webp';
  }
  
  if (originalFormat === 'png') {
    return 'png';
  }
  
  return 'jpeg';
};
